import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Spinner, Badge } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import type { AddToOrderDetail } from '@tkpod/options-widget';
import { OptionSelector } from '@/components/catalog/OptionSelector';
import { PortalProductOptions } from '@/components/catalog/PortalProductOptions';

// Designer SPA origin. Read from env so dev / staging / prod all
// resolve to their own deployments without code changes. Used for the
// STOREFRONT cart-flow launch path which navigates directly without
// minting a launch token (see action-button onClick below for why).
const DESIGNER_URL = import.meta.env.VITE_DESIGNER_URL as string;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OptionValue {
  id: string;
  name: string;
  slug: string;
  displayName: string;
  metadata: Record<string, unknown>;
  swatchUrl: string | null;
  mockupImageUrl: string | null;
  surcharge: string | null;
  sortOrder?: number;
}

interface ProductOption {
  optionTypeName: string;
  optionTypeSlug: string;
  uiType: string;
  position: number;
  values: OptionValue[];
}

interface VariantImage {
  type: string;
  url: string;
}

interface TemplateImage {
  url: string;
  thumbnailUrl: string;
  type: string;
  isDefault: boolean;
}

interface ProductVariant {
  id: string;
  sku: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  images: VariantImage[] | null;
  cost: string;
  weightOz: number | null;
  isActive: boolean;
  options: Record<string, string>;
}

interface PrintArea {
  slug: string;
  displayName: string;
  width: number;
  height: number;
  technique: string;
  isPrimary: boolean;
}

interface BlankProduct {
  id: string;
  brand: string;
  model: string;
  name: string;
  isPrimary: boolean;
  variants: ProductVariant[];
}

/**
 * UPT-level design context (mirrors UniversalProductTemplate.designContext
 * on the API). Drives the CTA rendering on this page:
 *   • STOREFRONT — end-customer designs at order-time (gang-sheet / DTF /
 *                  UV). CTA is "Design Now" → designer in cart-flow mode.
 *   • PRODUCT    — POD merchant designs once and publishes a product
 *                  (apparel "Create Design & Product"). CTA is the
 *                  product-creation flow with `?context=create-product`.
 *
 * Per-design rows on the API can carry richer values
 * (`MERCHANT_PRODUCT`, `MERCHANT_PRODUCT_LIBRARY`) but those are
 * server-assigned outcomes, never selectable here.
 */
type UPTDesignContext = 'STOREFRONT' | 'PRODUCT';

interface ProductDetail {
  id: string;
  name: string;
  slug: string;
  brand: string | null;
  modelName: string | null;
  description: string | null;
  material: string | null;
  fabricWeight: string | null;
  originCountry: string | null;
  thumbnailUrl: string | null;
  images: TemplateImage[] | null;
  productType: string;
  designerMode: string;
  designContext: UPTDesignContext;
  techniques: string[];
  options: ProductOption[];
  blankProducts: BlankProduct[];
  printAreas: PrintArea[];
  pricingProfile: { baseFee: string | null } | null;
}

// ---------------------------------------------------------------------------
// Gallery image type for unified display
// ---------------------------------------------------------------------------

interface GalleryImage {
  url: string;
  thumb: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const api = useApiClient();

  const [selections, setSelections] = useState<Record<string, string>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['catalog', 'product', slug],
    queryFn: () =>
      api.get<{ data: ProductDetail }>(`/api/v1/catalog/templates/${slug}`),
    enabled: !!slug,
  });

  const product = detailQuery.data?.data;

  const optionsQuery = useQuery({
    queryKey: ['catalog', 'product-options', product?.id],
    queryFn: () =>
      api.get<{ blockEnabled: boolean; options: unknown[] }>(
        `/api/v1/catalog/templates/${product!.id}/options-config`,
      ),
    enabled: product?.designerMode === 'NONE',
    retry: false,
  });

  const optionsBlockActive =
    product?.designerMode === 'NONE' &&
    optionsQuery.data?.blockEnabled === true &&
    (optionsQuery.data.options?.length ?? 0) > 0;
  const optionsPending = product?.designerMode === 'NONE' && optionsQuery.isLoading;

  const addOptionsToOrder = useCallback(
    (detail: AddToOrderDetail) => {
      const productName = [product?.brand, product?.modelName, product?.name].filter(Boolean).join(' ');
      const lines = [detail, ...(detail.moreLines ?? [])];
      navigate('/orders/new', {
        state: {
          selectedUptId: detail.uptId,
          selectedVariantId: detail.blankVariantId ?? undefined,
          optionSubmissionId: detail.optionSubmissionId,
          optionSummary: detail.summary,
          quantity: detail.quantity,
          unitPriceCents: detail.unitPriceCents,
          uploads: detail.uploads,
          productSlug: product?.slug,
          productName,
          ...(lines.length > 1
            ? {
                optionLines: lines.map((line) => ({
                  selectedVariantId: line.blankVariantId ?? undefined,
                  optionSubmissionId: line.optionSubmissionId,
                  optionSummary: line.summary,
                  quantity: line.quantity,
                  unitPriceCents: line.unitPriceCents,
                  uploads: line.uploads,
                  productSlug: product?.slug,
                  productName,
                })),
              }
            : {}),
        },
      });
    },
    [navigate, product?.slug],
  );

  const allVariants = useMemo(() => {
    if (!product) return [];
    return product.blankProducts.flatMap((bp) => bp.variants);
  }, [product]);

  const resolvedVariant = useMemo(() => {
    if (!product) return null;
    const optionSlugs = product.options.map((o) => o.optionTypeSlug);
    const allSelected = optionSlugs.every((s) => selections[s]);
    if (!allSelected) return null;

    return (
      allVariants.find((v) => {
        if (!v.isActive) return false;
        return optionSlugs.every((os) => {
          const selectedValueId = selections[os];
          const selectedVal = product.options
            .find((o) => o.optionTypeSlug === os)
            ?.values.find((val) => val.id === selectedValueId);
          if (!selectedVal) return false;
          return v.options[os] === selectedVal.slug;
        });
      }) ?? null
    );
  }, [product, selections, allVariants]);

  const computedPrice = useMemo(() => {
    if (!product || !resolvedVariant) return null;
    let price = parseFloat(resolvedVariant.cost);
    if (isNaN(price)) return null;
    for (const opt of product.options) {
      const selectedId = selections[opt.optionTypeSlug];
      const val = opt.values.find((v) => v.id === selectedId);
      if (val?.surcharge) {
        const s = parseFloat(val.surcharge);
        if (!isNaN(s)) price += s;
      }
    }
    return price;
  }, [product, resolvedVariant, selections]);

  const allOptionsSelected = useMemo(() => {
    if (!product) return false;
    return product.options.every((o) => selections[o.optionTypeSlug]);
  }, [product, selections]);

  // Build unified gallery: variant images > template gallery > color mockup > template thumbnail
  const gallery = useMemo((): GalleryImage[] => {
    const imgs: GalleryImage[] = [];
    const seen = new Set<string>();
    const add = (url: string, thumb: string, label: string) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      imgs.push({ url, thumb, label });
    };

    if (resolvedVariant) {
      if (resolvedVariant.imageUrl) {
        add(resolvedVariant.imageUrl, resolvedVariant.thumbnailUrl ?? resolvedVariant.imageUrl, 'Product');
      }
      if (resolvedVariant.images) {
        for (const img of resolvedVariant.images) {
          if (img.url) add(img.url, img.url, img.type);
        }
      }
    }

    if (product?.images) {
      const defaultImg = product.images.find((i) => i.isDefault);
      if (defaultImg) add(defaultImg.url, defaultImg.thumbnailUrl, 'Default');
      for (const img of product.images) {
        add(img.url, img.thumbnailUrl, img.type);
      }
    }

    if (product) {
      const colorOpt = product.options.find((o) => o.uiType === 'COLOR_SWATCH');
      const selectedColorId = colorOpt ? selections[colorOpt.optionTypeSlug] : null;
      if (selectedColorId) {
        const val = colorOpt?.values.find((v) => v.id === selectedColorId);
        if (val?.mockupImageUrl) add(val.mockupImageUrl, val.mockupImageUrl, 'Mockup');
      }
    }

    if (product?.thumbnailUrl && imgs.length === 0) {
      add(product.thumbnailUrl, product.thumbnailUrl, 'Product');
    }

    return imgs;
  }, [resolvedVariant, product, selections]);

  const handleOptionChange = useCallback((typeSlug: string, valueId: string) => {
    setSelections((prev) => ({ ...prev, [typeSlug]: valueId }));
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    setActiveIndex((prev) => (prev >= gallery.length ? 0 : prev));
  }, [gallery.length]);

  const activeImage = gallery[activeIndex] ?? gallery[0] ?? null;

  // ── Loading / Error states ──────────────────────────────────────────────

  if (detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Spinner size="lg" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <div className="rounded-full bg-danger-50 p-4">
          <svg className="h-8 w-8 text-danger-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-text-primary">Failed to load product</p>
          <p className="mt-1 text-sm text-text-tertiary">Please check your connection and try again.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/catalog')}>Back to Catalog</Button>
          <Button size="sm" onClick={() => void detailQuery.refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <svg className="h-16 w-16 text-text-tertiary/30" fill="none" viewBox="0 0 24 24" strokeWidth={0.8} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
        <p className="text-sm text-text-tertiary">Product not found.</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/catalog')}>Back to Catalog</Button>
      </div>
    );
  }

  // ── Main Render ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-text-tertiary">
        <button type="button" onClick={() => navigate('/catalog')} className="hover:text-text-primary transition-colors">
          Catalog
        </button>
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
        <span className="text-text-primary font-medium truncate max-w-[300px]">{product.name}</span>
      </nav>

      {/* Product Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

        {/* ── Left: Image Gallery ── */}
        <div>
          <div className="sticky top-6">
            {/* Main Image */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-surface-secondary">
              <div className="aspect-[4/3] flex items-center justify-center p-6">
                {activeImage ? (
                  <img
                    src={activeImage.url}
                    alt={product.name}
                    className="max-h-full max-w-full object-contain transition-opacity duration-300"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-4 text-text-tertiary/40">
                    <svg className="h-20 w-20" fill="none" viewBox="0 0 24 24" strokeWidth={0.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
                    </svg>
                    <span className="text-sm font-medium">No image available</span>
                  </div>
                )}
              </div>

              {/* Image counter */}
              {gallery.length > 1 && (
                <div className="absolute bottom-3 right-3 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                  {activeIndex + 1} / {gallery.length}
                </div>
              )}

              {/* Navigation arrows */}
              {gallery.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveIndex((i) => (i === 0 ? gallery.length - 1 : i - 1))}
                    className="absolute left-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-text-primary shadow-sm backdrop-blur-sm transition-all hover:bg-white hover:shadow-md"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveIndex((i) => (i === gallery.length - 1 ? 0 : i + 1))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-text-primary shadow-sm backdrop-blur-sm transition-all hover:bg-white hover:shadow-md"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail strip */}
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-2.5 overflow-x-auto pb-1">
                {gallery.map((img, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={img.url}
                      type="button"
                      onClick={() => setActiveIndex(idx)}
                      className={`shrink-0 rounded-md p-0.5 transition-all duration-150 ${
                        isActive ? 'bg-brand-600' : 'bg-transparent hover:bg-border'
                      }`}
                    >
                      <img
                        src={img.thumb}
                        alt={img.label}
                        className={`h-12 w-12 rounded object-cover transition-opacity ${
                          isActive ? 'opacity-100' : 'opacity-50 hover:opacity-80'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Product Info ── */}
        <div>
          <div className="space-y-6">
            {/* Brand & Title */}
            <div>
              {product.brand && (
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-600">
                  {product.brand}
                </p>
              )}
              <h1 className="mt-1 text-2xl font-bold leading-tight text-text-primary lg:text-3xl">
                {product.modelName ? `${product.modelName} ` : ''}{product.name}
              </h1>

              {/* Price preview */}
              {computedPrice !== null && (
                <p className="mt-2 text-2xl font-bold text-brand-600">
                  ${computedPrice.toFixed(2)}
                  <span className="ml-1.5 text-sm font-normal text-text-tertiary">per unit</span>
                </p>
              )}
            </div>

            {/* Specs row */}
            {(product.material || product.fabricWeight || product.originCountry) && (
              <div className="flex flex-wrap gap-3">
                {product.material && (
                  <div className="rounded-lg bg-surface-secondary px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">Material</p>
                    <p className="text-sm font-medium text-text-primary">{product.material}</p>
                  </div>
                )}
                {product.fabricWeight && (
                  <div className="rounded-lg bg-surface-secondary px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">Weight</p>
                    <p className="text-sm font-medium text-text-primary">{product.fabricWeight}</p>
                  </div>
                )}
                {product.originCountry && (
                  <div className="rounded-lg bg-surface-secondary px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">Origin</p>
                    <p className="text-sm font-medium text-text-primary">{product.originCountry}</p>
                  </div>
                )}
              </div>
            )}

            {/* Techniques */}
            {product.techniques.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {product.techniques.map((tech) => (
                  <Badge key={tech} variant="default" size="sm">
                    {tech.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            )}

            {/* Description */}
            {product.description && (
              <p className="text-sm leading-relaxed text-text-secondary">
                {product.description}
              </p>
            )}

            {/* Divider */}
            <div className="border-t border-border" />

            {optionsPending ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : product.designerMode === 'NONE' && optionsQuery.isError ? (
              <div className="rounded-xl border border-danger/20 bg-danger-light px-4 py-4">
                <p className="text-sm font-medium text-text-primary">Product options could not be loaded.</p>
                <p className="mt-1 text-sm text-text-secondary">
                  Choose options again after this loads. The standard product selector stays hidden so a partial order is not created.
                </p>
                <Button className="mt-3" size="sm" variant="outline" onClick={() => void optionsQuery.refetch()}>
                  Retry
                </Button>
              </div>
            ) : optionsBlockActive ? (
              <PortalProductOptions uptId={product.id} onAddToOrder={addOptionsToOrder} />
            ) : (
            <>
            {/* Options */}
            {product.options.length > 0 && (
              <div className="space-y-5">
                {[...product.options]
                  .sort((a, b) => a.position - b.position)
                  .map((opt) => (
                    <OptionSelector
                      key={opt.optionTypeSlug}
                      type={opt.optionTypeName}
                      uiType={opt.uiType}
                      values={opt.values}
                      selectedId={selections[opt.optionTypeSlug] ?? null}
                      onChange={(valueId) => handleOptionChange(opt.optionTypeSlug, valueId)}
                    />
                  ))}
              </div>
            )}

            {/* Price & Availability card */}
            {allOptionsSelected && (
              <div className="overflow-hidden rounded-xl border border-border">
                {resolvedVariant ? (
                  <div className="bg-gradient-to-r from-brand-50 to-brand-50/30 p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-text-primary">Unit Price</p>
                        <p className="mt-0.5 text-xs text-text-tertiary font-mono">
                          SKU: {resolvedVariant.sku}
                        </p>
                      </div>
                      {computedPrice !== null ? (
                        <div className="text-right">
                          <span className="text-3xl font-bold text-text-primary">
                            ${computedPrice.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-text-tertiary">Contact for pricing</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-warning-50/60 p-4">
                    <div className="flex items-start gap-3">
                      <svg className="mt-0.5 h-5 w-5 shrink-0 text-warning-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-warning-800">Combination unavailable</p>
                        <p className="mt-0.5 text-xs text-warning-600">Try selecting different options.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Action Buttons ───────────────────────────────────────────
                The CTA is driven by the UPT's `designContext` axis (NOT
                `designerMode`), because designContext is the merchant-
                facing classification the admin already chose at template
                setup time:

                  • PRODUCT    → "Create Design & Product" — POD merchant
                                 designs once and publishes a product
                                 (atomic Design + Product + Variants on
                                 submit). Same-tab redirect because the
                                 dialog returns the merchant to /products.
                  • STOREFRONT → "Design Now" — gang-sheet / DTF / UV
                                 cart-add flow. New tab keeps the catalog
                                 page open while designing (historical
                                 UX). No `context=create-product` so the
                                 designer renders the order panel.
                  • designerMode='NONE' → "Add to Order" (no designer
                                          required, plain blanks).

                Both designer launches go through the launch-token mint
                endpoint so a merchant identity NEVER travels in the URL.
                The token swap on the designer side seeds the
                `tk_designer_session` cookie. */}
            {product.designerMode === 'NONE' ? (
              <Button
                className="w-full"
                size="lg"
                disabled={!allOptionsSelected || !resolvedVariant}
                onClick={() => {
                  navigate('/orders/new', {
                    state: {
                      selectedUptId: product.id,
                      selectedVariantId: resolvedVariant?.id,
                      selectedOptions: selections,
                    },
                  });
                }}
              >
                Add to Order
                <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </Button>
            ) : (
              <>
                <Button
                  className="w-full"
                  size="lg"
                  disabled={!allOptionsSelected || !resolvedVariant || launching}
                  onClick={async () => {
                    if (!resolvedVariant) return;
                    setLaunchError(null);

                    const isProductContext = product.designContext === 'PRODUCT';

                    // STOREFRONT cart-flow launch (gang-sheet / DTF / UV)
                    // is intentionally a *direct* navigation — NO launch
                    // token mint. Reasons:
                    //   1. The designer's storefront cart endpoints are
                    //      authenticated by the merchant's portal cookie
                    //      (Domain=.transferkingdompod.com), which is
                    //      already in the browser when the merchant is
                    //      on this Portal page. A `tk_designer_session`
                    //      cookie adds nothing here.
                    //   2. The launch-token mint is itself a CSRF-
                    //      protected, cookie-bound POST. Calling it from
                    //      a flow that doesn't actually need it just
                    //      adds a network hop and a CSRF failure mode
                    //      (observed in prod as a 403 on the mint).
                    //   3. The URL carries only `uptId / variantId /
                    //      returnUrl`, none of which are secrets — so
                    //      removing the mint does not regress the
                    //      "no merchant identity in URL" property the
                    //      mint was introduced for. That property only
                    //      mattered for the apparel Create Product
                    //      flow's atomic-create endpoint, which is
                    //      designer-session-cookie protected.
                    if (!isProductContext) {
                      const params = new URLSearchParams({ uptId: product.id });
                      if (resolvedVariant) {
                        params.set('variantId', resolvedVariant.id);
                      }
                      params.set('returnUrl', window.location.href);
                      window.open(
                        `${DESIGNER_URL}/designer?${params.toString()}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                      return;
                    }

                    // PRODUCT context — mint a single-use launch JWT
                    // (5 min, scope=designer-launch). The designer SPA
                    // exchanges it for a `tk_designer_session` cookie
                    // on boot (POST /v1/auth/designer-session). That
                    // cookie is REQUIRED to call the atomic Create
                    // Product endpoint (`/v1/designs/and-product`), so
                    // the mint is non-optional here.
                    setLaunching(true);
                    try {
                      const res = await api.post<{
                        data: {
                          token: string;
                          designerUrl: string;
                          expiresAt: number;
                        };
                      }>('/api/v1/auth/designer-launch-token', {
                        uptId: product.id,
                        returnUrl: window.location.href,
                        purpose: 'create-product',
                      });

                      const { token, designerUrl } = res.data;
                      const params = new URLSearchParams({
                        uptId: product.id,
                        token,
                        context: 'create-product',
                      });
                      if (resolvedVariant) {
                        params.set('variantId', resolvedVariant.id);
                      }
                      params.set('returnUrl', window.location.href);
                      window.location.assign(
                        `${designerUrl}/designer?${params.toString()}`,
                      );
                    } catch (err) {
                      setLaunching(false);
                      setLaunchError(
                        err instanceof Error
                          ? err.message
                          : 'Failed to launch the designer. Please try again.',
                      );
                    }
                  }}
                >
                  {product.designContext === 'PRODUCT' ? (
                    <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                    </svg>
                  ) : (
                    <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                    </svg>
                  )}
                  {launching
                    ? 'Opening designer…'
                    : product.designContext === 'PRODUCT'
                      ? 'Create Design & Product'
                      : 'Design Now'}
                </Button>

                {/* Helper caption clarifies the two flows so merchants
                    understand why the same designer page produces two
                    different outcomes depending on the template type. */}
                <p className="text-center text-xs text-text-tertiary">
                  {product.designContext === 'PRODUCT'
                    ? 'Design once, publish as a product on your store.'
                    : 'Open the designer and add your design to a new order.'}
                </p>

                {launchError && (
                  <p className="text-center text-xs text-danger" role="alert">
                    {launchError}
                  </p>
                )}
              </>
            )}

            {!allOptionsSelected && product.options.length > 0 && (
              <p className="text-center text-xs text-text-tertiary">
                Select all options to see pricing and add to order
              </p>
            )}
            </>
            )}

            {/* Print Areas */}
            {product.printAreas.length > 0 && (
              <div className="rounded-xl border border-border p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                  Available Print Areas
                </h3>
                <div className="space-y-2">
                  {product.printAreas.map((pa) => (
                    <div key={pa.slug} className="flex items-center justify-between rounded-lg bg-surface-secondary px-3 py-2">
                      <div className="flex items-center gap-2">
                        {pa.isPrimary && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                        <span className="text-sm font-medium text-text-primary">{pa.displayName}</span>
                      </div>
                      <span className="text-xs text-text-tertiary">
                        {pa.width}&quot; &times; {pa.height}&quot;
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
